import httpClient from './httpClient';
import { ENDPOINTS } from '@shared/services/api/endpoints';
import { BASE_DOMAIN, PORTAL_HOST } from '@shared/constants/config';

/**
 * Multi-tenant subdomain SSO helpers.
 *
 * The SPA resolves the tenant (Firm) from the browser hostname BEFORE initializing Keycloak:
 *  - A company subdomain (e.g. `acme` in `acme.benchmarkit.solutions`) → resolve its Keycloak IdP
 *    and pin it via `idpHint` at login.
 *  - The main domain (`portal.benchmarkit.solutions`, `www.*`, `localhost`) → generic login + picker.
 *
 * All HTTP goes through `httpClient`. The `tenant_sub` / `idp_alias` cookies are set by the BACKEND
 * (non-HttpOnly) — this module only reads them for domain-change detection.
 */

const TENANT_SUB_COOKIE = 'tenant_sub';
const IDP_ALIAS_COOKIE = 'idp_alias';

/**
 * True when a host label is NOT a tenant subdomain: `www`, or the main portal host's own label
 * derived from `PORTAL_HOST` (e.g. `PORTAL_HOST=portal.<base>` reserves `portal`). Deriving the
 * reserved label from configuration — instead of hardcoding one — keeps subdomain resolution
 * correct in every environment (local, staging, prod) without code changes.
 * @param {string} label the leftmost host label
 */
function isReservedPrefix(label) {
  if (label === 'www') return true;
  const base = (BASE_DOMAIN || '').toLowerCase();
  const portalHost = (PORTAL_HOST || '').toLowerCase();
  if (base && portalHost && portalHost !== base && portalHost.endsWith(`.${base}`)) {
    const portalPrefix = portalHost.slice(0, -(base.length + 1)).split('.')[0];
    return label === portalPrefix;
  }
  return false;
}

/**
 * Resolve the company subdomain from a hostname (defaults to the current browser hostname).
 * @param {string} [hostname] override, primarily for tests
 * @returns {string|null} the lowercased subdomain (e.g. `"acme"`), or `null` for the main domain.
 */
export function getSubdomain(hostname = globalThis.location?.hostname) {
  const host = (hostname || '').toLowerCase();

  // No host, localhost (and *.localhost) → treated as main domain.
  if (!host || host === 'localhost' || host.endsWith('.localhost')) return null;

  // Without a configured base domain there is no subdomain concept — behave as main domain.
  const base = (BASE_DOMAIN || '').toLowerCase();
  if (!base) return null;

  // Apex / unrelated host → main domain.
  if (host === base || !host.endsWith(`.${base}`)) return null;

  const prefix = host.slice(0, -(base.length + 1)); // strip ".<base>"
  if (!prefix) return null;

  const first = prefix.split('.')[0];
  if (!first || isReservedPrefix(first)) return null;

  return first;
}

/**
 * Build a URL on the MAIN (non-tenant) portal host, preserving protocol + port. Used to send a
 * subdomain visitor back to generic login + the company picker — e.g. after logout, a subdomain
 * must NOT be the post-logout target or the bootstrap re-pins the tenant IdP and re-enters the same
 * company. Falls back to the current origin when no base domain is configured (pure-localhost dev).
 * @param {string} [path='/'] path (with leading slash) to append
 * @returns {string} absolute URL on the main portal host
 */
export function buildMainDomainUrl(path = '/') {
  const { protocol, port, origin } = globalThis.location ?? {};
  if (!PORTAL_HOST) return `${origin}${path}`;
  const portSuffix = port ? `:${port}` : '';
  return `${protocol}//${PORTAL_HOST}${portSuffix}${path}`;
}

/**
 * Safe tenant host label: lowercase letters and digits only, anchored to the whole string, max 63
 * (the DNS label limit). Deliberately self-contained rather than reusing the typing-feedback
 * validator in `@shared/utils/subdomain` — this is a SECURITY gate for URL construction, so it must
 * not depend on shared mutable regex state and must reject every host-breaking character
 * (`/`, `#`, `?`, `@`, `.`, `:`) outright.
 */
const SAFE_TENANT_LABEL = /^[a-z0-9]{1,63}$/;

/**
 * Build an absolute URL on a company's tenant subdomain (`{sub}.{BASE_DOMAIN}`), preserving the
 * current protocol + port. Returns `null` when the subdomain is missing/malformed or no base domain
 * is configured — callers MUST treat `null` as "do not redirect". An unvalidated value could break
 * out of the intended host (e.g. `evil.com#` → `https://evil.com#.<base>/`, an open redirect).
 * @param {string} subdomain company subdomain label
 * @param {string} [path='/'] path (with leading slash) to append
 * @returns {string|null} absolute tenant URL, or `null` when unsafe/unconfigurable
 */
export function buildTenantUrl(subdomain, path = '/') {
  const sub = (subdomain || '').trim().toLowerCase();
  const base = (BASE_DOMAIN || '').toLowerCase();
  if (!sub || !base || !SAFE_TENANT_LABEL.test(sub)) return null;
  const { protocol, port } = globalThis.location ?? {};
  const portSuffix = port ? `:${port}` : '';
  return `${protocol}//${sub}.${base}${portSuffix}${path}`;
}

/**
 * Resolve a tenant from the pre-auth API. Returns `null` for an unknown/inactive subdomain (404).
 * @param {string} subdomain
 * @returns {Promise<{subdomain:string, companyId:number, companyName:string|null, idpAlias:string, isActive:boolean}|null>}
 */
export async function resolveTenant(subdomain) {
  if (!subdomain) return null;

  try {
    const response = await httpClient.get(ENDPOINTS.TENANTS.RESOLVE(subdomain));
    return response.data?.data ?? null;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
}

/** Reads a cookie value by name from `document.cookie`, or `null` when absent. */
export function readCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const escaped = name.replaceAll(/[.$?*|{}()[\]\\/+^]/g, String.raw`\$&`);
  const match = new RegExp(`(?:^|; )${escaped}=([^;]*)`).exec(document.cookie);
  return match ? decodeURIComponent(match[1]) : null;
}

/** The `tenant_sub` cookie value the backend set on the previous visit, or `null`. */
export function getTenantSubCookie() {
  return readCookie(TENANT_SUB_COOKIE);
}

/** The `idp_alias` cookie value the backend set, or `null`. */
export function getIdpAliasCookie() {
  return readCookie(IDP_ALIAS_COOKIE);
}

/**
 * True when the current subdomain differs from the `tenant_sub` cookie set on a previous visit —
 * i.e. the user opened a different company's portal than last time.
 * @param {string|null} subdomain current subdomain (from {@link getSubdomain})
 */
export function isDomainChanged(subdomain) {
  const prev = getTenantSubCookie();
  return Boolean(prev && subdomain && prev.toLowerCase() !== subdomain.toLowerCase());
}
